import { useMutation } from "@tanstack/react-query";
import UserService from "../../service/UserService";
import { openLoadingTab } from "../../utils/HTMLUtils";

/** @typedef {{ siteId?: string, tab: Window | null }} OpenBdowVariables */

const NO_CONNECTION_STATUS = 404;

/**
 * Signs the user into BDOW! in a new tab.
 *
 * @returns {{
 *   openBdow: (siteId?: string) => void, // deep-links to the site when given, else the dashboard
 *   isOpening: boolean,
 *   isError: boolean,
 *   isConnectionMissing: boolean, // a dead or absent connection, rather than an outage
 * }}
 */
const useBdowLogin = () => {
  const { mutate, isPending, isError, error } = useMutation({
    /** @param {OpenBdowVariables} variables */
    mutationFn: ({ siteId }) => UserService.createBdowLoginLink({ siteId }),
    onSuccess: (/** @type {{ url: string }} */ { url }, { tab }) => {
      if (tab) {
        tab.location.href = url;
      } else if (!window.open(url, "_blank", "noopener")) {
        // Both opens popup-blocked; fail the mutation so isError surfaces instead of silently burning the single-use link.
        throw new Error("Popup blocked");
      }
    },
    onError: (_, { tab }) => tab?.close(),
  });

  /** @param {string} [siteId] */
  const openBdow = (siteId) => {
    mutate({ siteId, tab: openLoadingTab("Opening BDOW!...") });
  };

  return {
    openBdow,
    isOpening: isPending,
    isError,
    isConnectionMissing: !!error && "status" in error && error.status === NO_CONNECTION_STATUS,
  };
};

export default useBdowLogin;
